import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Tooltip } from "antd";
import { PlusCircleOutlined } from "@ant-design/icons";
import axios, { AxiosError, AxiosResponse } from "axios";
import { useTranslation } from "react-i18next";
import { ColumnsType } from "antd/lib/table";
import api from "../../../services/api";
import { useToast } from "../../../hooks/toast";
import Table from "../../../components/Table";
import AddAllocationModal from "./FactoryModal";
import UserAvatar from "../../../components/UserAvatar";

export interface IAllocation {
  id: string;
  name_user: string;
  avatar_url_user: string;
  percentage: number;
  total: number;
  user_id: string;
}

const FactoryRectability: React.FC = (): JSX.Element => {
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [editedAllocation, setEditedAllocation] = useState<
    IAllocation | undefined
  >();
  const [rectabilityAllocation, setRectabilityAllocation] = useState<
    IAllocation[]
  >([]);
  const { t } = useTranslation(["rectability"]);
  const { addToast } = useToast();

  const generateErrorToast = useCallback(
    (response: AxiosResponse | undefined, isDeleting?: boolean) => {
      let description;

      switch (response?.status) {
        case 403:
          description = t("requestError.403");
          break;
        case 401:
          description = t("requestError.401");
          break;
        case 404:
          description = t("requestError.404");
          break;
        case 409:
          description = t("requestError.409");
          break;
        default:
          description = isDeleting
            ? t("requestError.delete")
            : t("requestError.get");
      }

      addToast({
        type: "error",
        title: isDeleting
          ? t("requestError.deleteStatus")
          : t("requestError.getStatus"),
        description,
      });
    },
    [addToast, t]
  );
  const handleCloseModal = useCallback(
    (editFactory?: IAllocation[]) => {
      setEditedAllocation(undefined);
      if (editFactory) {
        const a = editFactory.map((usr) => {
          const newNumber = new Intl.NumberFormat("pt-BR");

          return { ...usr, total: newNumber.format(Number(usr.total)) };
        });
        setRectabilityAllocation((a as unknown) as IAllocation[]);
        // eslint-disable-next-line no-shadow
      }
      setModalVisible(false);
    },
    [rectabilityAllocation]
  );

  useEffect(() => {
    setLoading(true);
    api
      .get<IAllocation[]>("/factory")
      .then((response) => {
        const a = response.data.map((usr) => {
          const newNumber = new Intl.NumberFormat("pt-BR");

          return { ...usr, total: newNumber.format(Number(usr.total)) };
        });
        setRectabilityAllocation((a as unknown) as IAllocation[]);
      })
      .catch((err) => {
        if (axios.isAxiosError(err)) {
          const serverError = err as AxiosError;
          generateErrorToast(serverError.response);
        }
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const columnsFactory: ColumnsType<IAllocation> = [
    {
      title: t("factory.name"),
      dataIndex: "name_user",
    },
    {
      title: t("factory.avatar"),
      dataIndex: "avatar_url_user",
      render: (url: string, record) => {
        return record.name_user ? (
          <UserAvatar name={record?.name_user || ""} url={url} size={36} />
        ) : (
          <></>
        );
      },
    },
    {
      title: t("factory.porcentagem"),
      dataIndex: "percentage",
      render: (porcentagem) => `${porcentagem}%`,
    },
    {
      title: t("factory.total"),
      dataIndex: "total",
    },
    /*  {
      title: "Ações",
      dataIndex: "actions",
      fixed: "right",
      render: (_, record) => (
        <>
          <Button
            icon={<EditOutlined />}
            type="link"
            onClick={() => handleEdit(record)}
          />
        </>
      ),
    }, */
  ];
  const extraActionOpex = useMemo(() => {
    return (
      <Tooltip title={t("factory.add")}>
        <Button
          icon={<PlusCircleOutlined />}
          type="link"
          size="large"
          onClick={() => setModalVisible(true)}
        />
      </Tooltip>
    );
  }, [t]);
  return (
    <>
      <AddAllocationModal
        visible={modalVisible}
        handleCloseModal={handleCloseModal}
        opex={editedAllocation}
        state={rectabilityAllocation}
      />
      <Table
        data={rectabilityAllocation || []}
        columns={columnsFactory}
        title={<>{t("factory.title")}</>}
        extraActions={extraActionOpex}
        footer={() => <></>}
        loading={loading}
        pagination={false}
        extraFilters={<></>}
      />
    </>
  );
};

export default FactoryRectability;
